/** Inline SVG icons for the DOM overlay (crisp at any DPR, no image requests). */
import type { JSX } from 'preact';

type P = { size?: number; class?: string };

const ink = '#4a2c2a';

export function Coin({ size = 24, class: c }: P): JSX.Element {
  return (
    <svg class={c} width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <circle cx="16" cy="17" r="13" fill="#c98a00" />
      <circle cx="16" cy="15.5" r="13" fill="#ffcf3f" stroke={ink} stroke-width="2" />
      <circle cx="16" cy="15.5" r="8.5" fill="none" stroke="#e0a800" stroke-width="2" />
      <path d="M16 10l1.6 3.4 3.7.4-2.8 2.4.9 3.6-3.4-1.9-3.4 1.9.9-3.6-2.8-2.4 3.7-.4z" fill="#fff2b0" />
    </svg>
  );
}

export function Gem({ size = 24, class: c }: P): JSX.Element {
  return (
    <svg class={c} width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M8 6h16l5 6-13 16L3 12z" fill="#4cc9f0" stroke={ink} stroke-width="2" stroke-linejoin="round" />
      <path d="M3 12h26M11.5 6L16 28l4.5-22" fill="none" stroke="#1d3557" stroke-opacity=".45" stroke-width="1.5" />
      <ellipse cx="11" cy="9.5" rx="3" ry="1.4" fill="#fff" opacity=".6" />
    </svg>
  );
}

export function Star({ size = 24, class: c, empty }: P & { empty?: boolean }): JSX.Element {
  return (
    <svg class={c} width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path
        d="M16 3l3.9 8 8.7 1.2-6.3 6.1 1.5 8.7L16 22.9 8.2 27l1.5-8.7L3.4 12.2l8.7-1.2z"
        fill={empty ? 'rgba(74,44,42,.18)' : '#ffc400'}
        stroke={empty ? 'rgba(74,44,42,.45)' : ink}
        stroke-width="2"
        stroke-linejoin="round"
      />
      {!empty && <ellipse cx="12.5" cy="11" rx="2.6" ry="1.3" fill="#fff" opacity=".6" />}
    </svg>
  );
}

export function Shop({ size = 28, class: c }: P): JSX.Element {
  return (
    <svg class={c} width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect x="5" y="12" width="22" height="16" rx="2" fill="#fbe3c4" stroke={ink} stroke-width="2" />
      <path d="M3 12l3-7h20l3 7z" fill="#ff6b5b" stroke={ink} stroke-width="2" stroke-linejoin="round" />
      <path d="M9 5l-1.5 7M16 5v7M23 5l1.5 7" stroke="#fff" stroke-width="2" />
      <rect x="18" y="17" width="6" height="11" rx="1" fill="#ff9f1c" stroke={ink} stroke-width="2" />
      <rect x="8" y="17" width="7" height="6" rx="1" fill="#bfe9ff" stroke={ink} stroke-width="2" />
    </svg>
  );
}

export function Gear({ size = 26 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path
        d="M13.5 3h5l.8 3.6 2.6 1.1 3.1-2 3.5 3.5-2 3.1 1.1 2.6 3.6.8v5l-3.6.8-1.1 2.6 2 3.1-3.5 3.5-3.1-2-2.6 1.1-.8 3.6h-5l-.8-3.6-2.6-1.1-3.1 2-3.5-3.5 2-3.1-1.1-2.6L2 18.5v-5l3.6-.8 1.1-2.6-2-3.1 3.5-3.5 3.1 2 2.6-1.1z"
        fill="#fff8ec"
        stroke={ink}
        stroke-width="2"
        stroke-linejoin="round"
      />
      <circle cx="16" cy="16" r="4.5" fill="#ffc845" stroke={ink} stroke-width="2" />
    </svg>
  );
}

export function Pause({ size = 24 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect x="7" y="6" width="6.5" height="20" rx="2.5" fill="#fff8ec" stroke={ink} stroke-width="2" />
      <rect x="18.5" y="6" width="6.5" height="20" rx="2.5" fill="#fff8ec" stroke={ink} stroke-width="2" />
    </svg>
  );
}

export function Back({ size = 24 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M19 6L9 16l10 10" fill="none" stroke={ink} stroke-width="7" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M19 6L9 16l10 10" fill="none" stroke="#fff8ec" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

export function Check({ size = 22 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <circle cx="16" cy="16" r="13" fill="#3bb273" stroke={ink} stroke-width="2" />
      <path d="M9.5 16.5l4.5 4.5 8.5-9" fill="none" stroke="#fff" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

export function Lock({ size = 22 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M10 14v-3a6 6 0 0112 0v3" fill="none" stroke={ink} stroke-width="3" />
      <rect x="6.5" y="13.5" width="19" height="14" rx="3" fill="#ffc845" stroke={ink} stroke-width="2" />
    </svg>
  );
}

export function Play({ size = 26 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M10 6.5v19a1.5 1.5 0 002.3 1.3l14.6-9.5a1.5 1.5 0 000-2.6L12.3 5.2A1.5 1.5 0 0010 6.5z" fill="#fff" stroke={ink} stroke-width="2" stroke-linejoin="round" />
    </svg>
  );
}

export function AdTv({ size = 22 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect x="3" y="7" width="26" height="19" rx="4" fill="#8e6cef" stroke={ink} stroke-width="2" />
      <path d="M13 12v9l7.5-4.5z" fill="#fff" />
      <path d="M11 3l5 4 5-4" fill="none" stroke={ink} stroke-width="2" stroke-linecap="round" />
    </svg>
  );
}

export function Gift({ size = 64 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <rect x="8" y="26" width="48" height="32" rx="5" fill="#ff6b5b" stroke={ink} stroke-width="3" />
      <rect x="4" y="16" width="56" height="14" rx="4" fill="#ff8b7a" stroke={ink} stroke-width="3" />
      <rect x="28" y="16" width="8" height="42" fill="#ffc845" stroke={ink} stroke-width="3" />
      <path d="M32 16c-6-10-18-10-16-2 1 4 10 3 16 2zM32 16c6-10 18-10 16-2-1 4-10 3-16 2z" fill="#ffc845" stroke={ink} stroke-width="3" />
    </svg>
  );
}

export function Flame({ size = 22 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M16 3c2 6 9 8 9 16a9 9 0 01-18 0c0-5 3-7 4-10 1 3 2 4 4 5 1-4 0-7 1-11z" fill="#ff9f1c" stroke={ink} stroke-width="2" stroke-linejoin="round" />
      <path d="M16 17c2 2 4 3 4 6a4 4 0 01-8 0c0-2 2-3 4-6z" fill="#ffd23f" />
    </svg>
  );
}

export function Booster({ kind, size = 34 }: { kind: string; size?: number }): JSX.Element {
  const body: Record<string, JSX.Element> = {
    undo: (
      <g>
        <path d="M12 9a10 10 0 11-3.5 13" fill="none" stroke={ink} stroke-width="7" stroke-linecap="round" />
        <path d="M12 9a10 10 0 11-3.5 13" fill="none" stroke="#3a86ff" stroke-width="3.5" stroke-linecap="round" />
        <path d="M4 5l9 2-6 7z" fill="#3a86ff" stroke={ink} stroke-width="2" stroke-linejoin="round" />
      </g>
    ),
    shuffle: (
      <g fill="none" stroke-linecap="round">
        <path d="M4 8c10 0 14 16 24 16M4 24c10 0 14-16 24-16" stroke={ink} stroke-width="6.5" />
        <path d="M4 8c10 0 14 16 24 16" stroke="#ff9f1c" stroke-width="3" />
        <path d="M4 24c10 0 14-16 24-16" stroke="#8e6cef" stroke-width="3" />
      </g>
    ),
    magnet: (
      <g>
        <path d="M7 16V12a9 9 0 0118 0v4" fill="none" stroke={ink} stroke-width="10" />
        <path d="M7 16V12a9 9 0 0118 0v4" fill="none" stroke="#ff5a5f" stroke-width="6" />
        <rect x="3.5" y="18" width="7" height="8" rx="1.5" fill="#dfe7ef" stroke={ink} stroke-width="2" />
        <rect x="21.5" y="18" width="7" height="8" rx="1.5" fill="#dfe7ef" stroke={ink} stroke-width="2" />
      </g>
    ),
    extraSlot: (
      <g>
        <rect x="3" y="10" width="22" height="17" rx="5" fill="#e9b57a" stroke={ink} stroke-width="2" />
        <circle cx="24" cy="10" r="6.5" fill="#3bb273" stroke={ink} stroke-width="2" />
        <path d="M21 10h6M24 7v6" stroke="#fff" stroke-width="2.4" stroke-linecap="round" />
      </g>
    ),
  };
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      {body[kind]}
    </svg>
  );
}

export function PipFace({ size = 96, mood = 'happy' }: { size?: number; mood?: 'happy' | 'worried' | 'sleepy' }): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <path d="M30 22 C30 6 46 6 46 20" fill="none" stroke="#8a5a36" stroke-width="5" stroke-linecap="round" />
      <path d="M54 20 C54 6 70 6 70 22" fill="none" stroke="#8a5a36" stroke-width="5" stroke-linecap="round" />
      <path d="M18 24 Q50 16 82 24 L86 86 Q50 96 14 86 Z" fill="#e9b57a" stroke={ink} stroke-width="4" stroke-linejoin="round" />
      <path d="M18 24 Q50 16 82 24 L81 34 Q50 27 19 34 Z" fill="#d9995a" />
      {mood === 'happy' ? (
        <g fill="none" stroke="#2b1a14" stroke-width="4" stroke-linecap="round">
          <path d="M31 56 q7 -8 14 0" />
          <path d="M55 56 q7 -8 14 0" />
          <path d="M40 68 Q50 80 60 68" />
        </g>
      ) : mood === 'worried' ? (
        <g>
          <ellipse cx="38" cy="55" rx="6" ry="8" fill="#2b1a14" />
          <ellipse cx="62" cy="55" rx="6" ry="8" fill="#2b1a14" />
          <path d="M40 72 q5 -5 10 0 q5 5 10 0" fill="none" stroke="#2b1a14" stroke-width="3.5" stroke-linecap="round" />
        </g>
      ) : (
        <g fill="none" stroke="#2b1a14" stroke-width="4" stroke-linecap="round">
          <path d="M31 56 q7 6 14 0" />
          <path d="M55 56 q7 6 14 0" />
          <path d="M44 70 h12" />
        </g>
      )}
      <ellipse cx="29" cy="67" rx="6" ry="3.5" fill="#ff8b7a" opacity=".7" />
      <ellipse cx="71" cy="67" rx="6" ry="3.5" fill="#ff8b7a" opacity=".7" />
    </svg>
  );
}

export function Mechanic({ kind, size = 120 }: { kind: string; size?: number }): JSX.Element {
  const tile = (x: number, y: number, fill: string, dim = false) => (
    <g opacity={dim ? 0.55 : 1}>
      <rect x={x} y={y} width="36" height="36" rx="9" fill="#fff8ec" stroke={ink} stroke-width="3" />
      <circle cx={x + 18} cy={y + 18} r="11" fill={fill} stroke={ink} stroke-width="2.5" />
    </g>
  );
  const scenes: Record<string, JSX.Element> = {
    clutter: (
      <g>
        {tile(14, 44, '#ff9f1c')}
        {tile(42, 44, '#3a86ff', true)}
        {tile(70, 44, '#ff9f1c')}
        <g class="mech-bounce">
          <circle cx="104" cy="30" r="12" fill="#ffcf3f" stroke={ink} stroke-width="2.5" />
          <text x="104" y="35" text-anchor="middle" font-size="14" font-weight="700" fill={ink}>
            +5
          </text>
        </g>
      </g>
    ),
    wrapped: (
      <g>
        {tile(42, 40, '#3bb273')}
        <g class="mech-lift">
          <rect x="40" y="38" width="40" height="40" rx="10" fill="#ff8fab" stroke={ink} stroke-width="3" />
          <path d="M60 40v36M42 58h36" stroke="#ffd6e0" stroke-width="6" />
          <text x="60" y="66" text-anchor="middle" font-size="18" font-weight="700" fill="#fff" stroke={ink} stroke-width="1">
            ?
          </text>
        </g>
      </g>
    ),
    twoCustomers: (
      <g>
        <circle cx="38" cy="50" r="20" fill="#f9d4b4" stroke={ink} stroke-width="3" />
        <circle cx="82" cy="50" r="20" fill="#e0ac69" stroke={ink} stroke-width="3" />
        <rect x="20" y="78" width="36" height="24" rx="6" fill="#fff" stroke={ink} stroke-width="3" class="mech-bounce" />
        <rect x="64" y="78" width="36" height="24" rx="6" fill="#fff" stroke={ink} stroke-width="3" class="mech-bounce2" />
      </g>
    ),
    rush: (
      <g>
        <circle cx="60" cy="62" r="34" fill="#fff8ec" stroke={ink} stroke-width="4" />
        <rect x="52" y="16" width="16" height="10" rx="3" fill="#ff6b5b" stroke={ink} stroke-width="3" />
        <path d="M60 62 L60 38" stroke={ink} stroke-width="5" stroke-linecap="round" class="mech-spin" />
      </g>
    ),
    crates: (
      <g class="mech-shake">
        <rect x="36" y="36" width="48" height="48" rx="8" fill="#c98a4b" stroke={ink} stroke-width="3" />
        <path d="M42 42l36 36M78 42L42 78" stroke="#8a5a36" stroke-width="6" />
      </g>
    ),
    ice: (
      <g>
        {tile(42, 42, '#8e6cef')}
        <rect x="40" y="40" width="40" height="40" rx="10" fill="rgba(170,225,255,.7)" stroke="#fff" stroke-width="3" class="mech-fade" />
        <path d="M50 46l10 14-4 14M60 60l14 4" stroke="#2878aa" stroke-width="3" fill="none" class="mech-fade" />
      </g>
    ),
    conveyor: (
      <g>
        <rect x="8" y="64" width="104" height="26" rx="13" fill="#6c757d" stroke={ink} stroke-width="3" />
        <g class="mech-slide">
          {tile(14, 30, '#ff5a5f')}
          {tile(54, 30, '#2ec4b6')}
        </g>
      </g>
    ),
  };
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" aria-hidden="true">
      {scenes[kind] ?? scenes.clutter}
    </svg>
  );
}

export function Calendar({ size = 26 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect x="4" y="7" width="24" height="21" rx="4" fill="#fff8ea" stroke={ink} stroke-width="2" />
      <path d="M4 13h24" stroke={ink} stroke-width="2" />
      <rect x="4" y="7" width="24" height="6" rx="3" fill="#ff6b5b" stroke={ink} stroke-width="2" />
      <path d="M10 4v6M22 4v6" stroke={ink} stroke-width="2.4" stroke-linecap="round" />
      <path d="M11 20l3 3 7-7" fill="none" stroke="#2ec4b6" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

export function Trophy({ size = 26 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M9 5h14v7a7 7 0 0 1-14 0z" fill="#ffc845" stroke={ink} stroke-width="2" stroke-linejoin="round" />
      <path d="M9 7H5c0 5 2 7 5 7M23 7h4c0 5-2 7-5 7" fill="none" stroke={ink} stroke-width="2" />
      <path d="M16 19v4M11 27h10l-1-4h-8z" fill="#e0a800" stroke={ink} stroke-width="2" stroke-linejoin="round" />
      <ellipse cx="13" cy="9" rx="1.6" ry="2.4" fill="#fff" opacity=".6" />
    </svg>
  );
}

export function Friends({ size = 26 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <circle cx="21" cy="11" r="4.5" fill="#8e6cef" stroke={ink} stroke-width="2" />
      <path d="M13 27c0-5 3.5-8 8-8s8 3 8 8z" fill="#8e6cef" stroke={ink} stroke-width="2" stroke-linejoin="round" />
      <circle cx="11" cy="12" r="5" fill="#2ec4b6" stroke={ink} stroke-width="2" />
      <path d="M2 28c0-6 4-9 9-9s9 3 9 9z" fill="#2ec4b6" stroke={ink} stroke-width="2" stroke-linejoin="round" />
    </svg>
  );
}

export function Wheel({ size = 26 }: P): JSX.Element {
  const c = ['#ff6b5b', '#ffc845', '#2ec4b6', '#8e6cef', '#ff70a6', '#4cc9f0'];
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      {c.map((col, i) => {
        const a0 = (i / 6) * Math.PI * 2;
        const a1 = ((i + 1) / 6) * Math.PI * 2;
        return <path key={i} d={`M16 16L${16 + 13 * Math.cos(a0)} ${16 + 13 * Math.sin(a0)}A13 13 0 0 1 ${16 + 13 * Math.cos(a1)} ${16 + 13 * Math.sin(a1)}Z`} fill={col} />;
      })}
      <circle cx="16" cy="16" r="13" fill="none" stroke={ink} stroke-width="2" />
      <circle cx="16" cy="16" r="3" fill="#fff" stroke={ink} stroke-width="2" />
      <path d="M16 1l3 5h-6z" fill="#fff" stroke={ink} stroke-width="1.6" stroke-linejoin="round" />
    </svg>
  );
}

export function Mail({ size = 26 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <rect x="3" y="8" width="26" height="18" rx="3" fill="#fff8ea" stroke={ink} stroke-width="2" />
      <path d="M4 10l12 9 12-9" fill="none" stroke={ink} stroke-width="2" stroke-linejoin="round" />
    </svg>
  );
}

export function ShareIcon({ size = 22 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <circle cx="24" cy="7" r="4" fill="currentColor" />
      <circle cx="8" cy="16" r="4" fill="currentColor" />
      <circle cx="24" cy="25" r="4" fill="currentColor" />
      <path d="M11 14l10-5M11 18l10 5" stroke="currentColor" stroke-width="2.6" />
    </svg>
  );
}

export function LinkIcon({ size = 22 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M13 19l6-6M14 9l3-3a5 5 0 0 1 7 7l-3 3M18 23l-3 3a5 5 0 0 1-7-7l3-3" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
    </svg>
  );
}

export function Cart({ size = 24 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M3 5h4l3 15h15l3-10H9" fill="none" stroke={ink} stroke-width="2.4" stroke-linejoin="round" stroke-linecap="round" />
      <circle cx="12" cy="26" r="2.4" fill={ink} />
      <circle cx="23" cy="26" r="2.4" fill={ink} />
    </svg>
  );
}

export function Plus({ size = 16 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8 2v12M2 8h12" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
    </svg>
  );
}

export function Globe({ size = 22 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <circle cx="16" cy="16" r="12" fill="#4cc9f0" stroke={ink} stroke-width="2" />
      <path d="M4 16h24M16 4c-5 6-5 18 0 24M16 4c5 6 5 18 0 24" fill="none" stroke={ink} stroke-width="1.6" />
    </svg>
  );
}

export function Shield({ size = 22 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M16 3l11 4v8c0 7-5 12-11 14C10 27 5 22 5 15V7z" fill="#2ec4b6" stroke={ink} stroke-width="2" stroke-linejoin="round" />
      <path d="M11 16l4 4 7-8" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  );
}

export function User({ size = 22 }: P): JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <circle cx="16" cy="11" r="6" fill="#ffc845" stroke={ink} stroke-width="2" />
      <path d="M5 28c0-6 5-10 11-10s11 4 11 10z" fill="#ff6b5b" stroke={ink} stroke-width="2" stroke-linejoin="round" />
    </svg>
  );
}
